import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Pagination from "@mui/material/Pagination";
import PaginationItem from "@mui/material/PaginationItem";
import Tooltip from "@mui/material/Tooltip";
import { ArrowLeftIcon, ArrowRightIcon } from "@mui/x-date-pickers-pro";
import { Fragment, useEffect, useState } from "react";
import useDeleteInvalidInput from "../../../QueryHooks/validate/useDeleteInvalidInput";
import useValidateSrf from "../../../QueryHooks/validate/useValidateSrf";
import ChipModal from "./ChipModal";
import { clearInvalidPageInputs, confirmSubmit, invalidPage } from "./helper";
import "./styles/page-values.css";

// Component that displays the values of the current page of the srf and allows the user to edit them
const PageValues = ({
  clientId,
  projectId,
  pageNumber,
  formData,
  setPageNumber,
  numPages,
  setHoveredChipName,
  updateSrf,
  enqueueSnackbar,
  invalidData,
  invalidFetched,
  setConfirmOpen,
  invalidInputs,
  setInvalidInputs,
  clearInvalid,
  setClearInvalid,
  srfFetching,
}) => {
  const [open, setOpen] = useState(false);
  const [openName, setOpenName] = useState("");
  const [page1, setPage1] = useState([]);
  const [page2, setPage2] = useState([]);
  const [page3, setPage3] = useState([]);

  // State to manage inputs that should be invalidated together when one of them is invalidated
  const [inputGroup, setInputGroup] = useState({
    p1Groups: {
      oName: ["oTaxId", "oAdd", "oCity", "oState", "oZip", "oPhone", "oFax"],
      oContactName: ["oContactTitle", "oContactPhone", "oContactEmail"],
      oAuthorizedContactName: ["oAuthorizedContactTitle"],
      gcName: [
        "gcTaxId",
        "gcAdd",
        "gcCity",
        "gcState",
        "gcZip",
        "gcPhone",
        "gcFax",
      ],
      gcContactName: ["gcContactTitle", "gcContactPhone", "gcContactEmail"],
      gcAuthorizedContactName: ["gcAuthorizedContactTitle"],
      billingCompany: [
        "companyType",
        "billingAdd",
        "billingCity",
        "billingState",
        "billingZip",
        "billingPhone",
        "billingFax",
      ],
      billingContactName: [
        "billingContactTitle",
        "billingContactPhone",
        "billingContactEmail",
      ],
    },
    p2Groups: {
      pAdd: ["pCity", "pCounty", "pState", "pZip"],
      siteContactName: ["siteContactPhone", "siteContactEmail"],
      projectManagerName: [
        "projectManagerCell",
        "projectManagerEmail",
        "projectManagerPhone",
      ],
    },
  });

  // Validate srf Query
  const { data: validData, isFetched: validFetched } = useValidateSrf(
    clientId,
    projectId,
  );

  // Set the page values to the valid data fetched
  useEffect(() => {
    if (validData && validFetched) {
      console.log(validData.page1);
      setPage1(() => validData.page1);
      setPage2(() => validData.page2);
      setPage3(() => validData.page3);
    }
  }, [validData, validFetched]);

  // Set the invalid inputs to the invalid data fetched
  useEffect(() => {
    if (invalidData && invalidFetched) {
      setInvalidInputs(() => invalidData);
    }
  }, [invalidData, invalidFetched]);

  // Delete invalid input mutation
  const deleteInvalidInput = useDeleteInvalidInput(
    clientId,
    projectId,
    enqueueSnackbar,
  );

  // Clear invalid inputs for the current page when the clearInvalid state is true
  useEffect(() => {
    if (clearInvalid === true) {
      clearInvalidPageInputs(
        [page1, page2, page3],
        pageNumber,
        invalidInputs,
        deleteInvalidInput,
        enqueueSnackbar,
      );
      setClearInvalid(() => false);
    }
  }, [clearInvalid]);

  return (
    <div id="page-values-container">
      <h1 id="page-values-header">{`Page ${pageNumber} Values`}</h1>
      <div id="values-container">
        <hr className="page-values-hr" />
        <div id="page-values">
          <div
            className={`page${pageNumber}`}
            id="chips-container"
            style={{
              pointerEvents: srfFetching ? "none !important" : "auto",
            }}
          >
            {validFetched && validData && pageNumber === 1
              ? Object.keys(page1).map((name, index) => {
                  return (
                    <Fragment key={`${name}-fragment`}>
                      <Tooltip
                        key={`${name}-tooltip-${index}`}
                        title={page1[name][0]}
                        placement="top"
                        arrow
                        classes={{
                          tooltip: "durationTooltip",
                          arrow: "durationArrow",
                          popper: "durationPopper",
                        }}
                      >
                        <Chip
                          key={`${name}-chip-${index}`}
                          id={`${name}-chip`}
                          style={{
                            pointerEvents: srfFetching ? "none" : "auto",
                          }}
                          className={`page-value-chip ${
                            invalidInputs.filter(
                              (input) => input?.name === name,
                            ).length > 0
                              ? "invalid-chip"
                              : "valid-chip"
                          }`}
                          label={page1[name][0]}
                          clickable={true}
                          onClick={() => {
                            setOpenName(() => name);
                            setOpen(() => true);
                          }}
                          onMouseEnter={() => {
                            srfFetching
                              ? setHoveredChipName(() => null)
                              : setHoveredChipName(() => name);
                          }}
                          onMouseLeave={() => {
                            setHoveredChipName(() => null);
                          }}
                        />
                      </Tooltip>
                      <ChipModal
                        key={`${name}-modal-${index}`}
                        clientId={clientId}
                        projectId={projectId}
                        name={name}
                        open={open}
                        setOpen={setOpen}
                        openName={openName}
                        setOpenName={setOpenName}
                        pageState={page1}
                        setPageState={setPage1}
                        invalidInputs={invalidInputs}
                        setInvalidInputs={setInvalidInputs}
                        formData={formData}
                        updateSrf={updateSrf}
                        enqueueSnackbar={enqueueSnackbar}
                        inputGroup={inputGroup.p1Groups}
                      />
                    </Fragment>
                  );
                })
              : validFetched && validData && pageNumber === 2
                ? Object.keys(page2).map((name, index) => {
                    return (
                      <Fragment key={`${name}-fragment`}>
                        <Chip
                          key={`${name}-chip-${index}`}
                          id={`${name}-chip`}
                          className={`page-value-chip ${
                            invalidInputs.filter(
                              (input) => input?.name === name,
                            ).length > 0
                              ? "invalid-chip"
                              : "valid-chip"
                          }`}
                          label={page2[name][0]}
                          style={
                            name === "emergencyContactCompany"
                              ? { display: "none" }
                              : {}
                          }
                          clickable={true}
                          onClick={() => {
                            setOpenName(() => name);
                            setOpen(() => true);
                          }}
                          onMouseEnter={() => {
                            setHoveredChipName(() => name);
                          }}
                          onMouseLeave={() => {
                            setHoveredChipName(() => null);
                          }}
                        />
                        <ChipModal
                          key={`${name}-modal-${index}`}
                          clientId={clientId}
                          projectId={projectId}
                          name={name}
                          open={open}
                          setOpen={setOpen}
                          openName={openName}
                          setOpenName={setOpenName}
                          pageState={page2}
                          setPageState={setPage2}
                          invalidInputs={invalidInputs}
                          setInvalidInputs={setInvalidInputs}
                          formData={formData}
                          updateSrf={updateSrf}
                          enqueueSnackbar={enqueueSnackbar}
                          inputGroup={inputGroup.p2Groups}
                        />
                      </Fragment>
                    );
                  })
                : validFetched && validData && pageNumber === 3
                  ? Object.keys(page3).map((name, index) => {
                      return (
                        <Fragment key={`${name}-fragment`}>
                          <Chip
                            key={`${name}-chip-${index}`}
                            id={`${name}-chip`}
                            className={`page-value-chip ${
                              invalidInputs.filter(
                                (input) => input?.name === name,
                              ).length > 0
                                ? "invalid-chip"
                                : "valid-chip"
                            }`}
                            label={page3[name][0]}
                            // style={
                            //   name === "specialServices"
                            //     ? { display: "none" }
                            //     : {}
                            // }
                            clickable={true}
                            onClick={() => {
                              setOpenName(() => name);
                              setOpen(() => true);
                            }}
                            onMouseEnter={() => {
                              setHoveredChipName(() => name);
                            }}
                            onMouseLeave={() => {
                              setHoveredChipName(() => null);
                            }}
                          />
                          <ChipModal
                            key={`${name}-modal-${index}`}
                            clientId={clientId}
                            projectId={projectId}
                            name={name}
                            open={open}
                            setOpen={setOpen}
                            openName={openName}
                            setOpenName={setOpenName}
                            pageState={page3}
                            setPageState={setPage3}
                            invalidInputs={invalidInputs}
                            setInvalidInputs={setInvalidInputs}
                            formData={formData}
                            updateSrf={updateSrf}
                            enqueueSnackbar={enqueueSnackbar}
                          />
                        </Fragment>
                      );
                    })
                  : null}
          </div>
          <div className={`pdf-btns-container pdf-page${pageNumber}`}>
            <Button
              variant="contained"
              id="validate-page-button"
              disabled={
                !invalidInputs ||
                invalidInputs.length === 0 ||
                !invalidPage(pageNumber, invalidInputs, [page1, page2, page3])
              }
              onClick={() => {
                clearInvalidPageInputs(
                  [page1, page2, page3],
                  pageNumber,
                  invalidInputs,
                  deleteInvalidInput,
                  enqueueSnackbar,
                );
              }}
              sx={{
                display:
                  invalidInputs.length === 0 ||
                  !invalidPage(pageNumber, invalidInputs, [page1, page2, page3])
                    ? "none"
                    : "flex",
              }}
            >
              Validate Page
            </Button>
            <Button
              id="pdf-submit"
              variant="contained"
              onClick={(e) => confirmSubmit(e, setConfirmOpen)}
              disabled={updateSrf.isLoading || pageNumber !== numPages}
              sx={{
                display: pageNumber !== numPages ? "none" : "flex",
              }}
            >
              Submit
            </Button>
          </div>
          <Pagination
            id="pdf-pagination"
            className="pdf-pagination"
            count={numPages}
            page={pageNumber}
            onChange={(e, val) => {
              setPageNumber(() => val);
              window.scrollTo(0, 0);
            }}
            size="large"
            shape="circular"
            classes={{
              root: "pdf-pagination-root",
              ul: "pdf-pagination-ul",
            }}
            renderItem={(item) => (
              <PaginationItem
                className="pdf-pagination-item"
                id={"pagination-item"}
                slots={{
                  previous: ArrowLeftIcon,
                  next: ArrowRightIcon,
                }}
                classes={{
                  root: "pdf-pagination-item-root",
                  page: "pdf-pagination-page",
                  previousNext: "pdf-pagination-icon",
                  text: "pdf-pagination-text",
                  rounded: "pdf-pagination-rounded",
                }}
                {...item}
              />
            )}
          />
        </div>
      </div>
    </div>
  );
};

export default PageValues;
